import './../../Player.css';
import React, {useContext, useEffect, useState, useRef} from 'react';
import { PlayerContext } from '../../PlayerContext';
import { openFullscreen, printObj, browserLocales, isAndroid, hasInstallablePixel } from '../../Utils';
import { analyticsManager, functions } from '../../../index';
import SecondaryButton, {chatType} from '../components/SecondaryButton';
import { useAnimate, motion, AnimatePresence } from 'framer-motion';
import { IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { buildAnswerChatStoryContext, hasChainRestaurants } from '../../chainRestaurantUtils';
import * as Sentry from "@sentry/react";
import WebPageMock from '../components/WebPageMock';
import { requestReopenPixelConsent, getPersistedPixelConsent, getStoredPixelConsent } from '../../PixelConsentOverlay';

function ChatButton(props){
    const { t } = useTranslation();
    const [isOpened, setIsOpened] = useState(false);
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [conversationHistory, setConversationHistory] = useState([]);
    const [anchoredCount, setAnchoredCount] = useState(0);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const inputRef = useRef(null);
    const bottomSheetRef = useRef(null);
    const [frameOpened, setFrameOpened] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    
    // Générer un identifiant aléatoire si ipHash n'existe pas
    const [generatedIpHash] = useState(() => {
        if (!props.story?.ipHash) {
            // Générer un identifiant aléatoire unique
            return 'chat_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15) + '_' + Date.now();
        }
        return null;
    });
    
    // Utiliser ipHash de la story ou l'identifiant généré
    const ipHash = props.story?.ipHash || generatedIpHash;

    const storyDimensions = useContext(PlayerContext).get("storyDimensions");
    const propagateFocusChange = useContext(PlayerContext).get("propagateFocusChange");
    const pauseVideo = useContext(PlayerContext).get("pauseVideo");
    const setActiveFeature = useContext(PlayerContext).get("setActiveFeature");
    const activeFeature = useContext(PlayerContext).get("activeFeature");
    const appAnalyticsManager = useContext(PlayerContext).get("appAnalyticsManager");
    const playMode = useContext(PlayerContext).get("playMode");
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const setPlayAudio = useContext(PlayerContext).get("setPlayAudio");
    const audioAlreadyDisabled = useContext(PlayerContext).get("audioAlreadyDisabled");
    const playAudio = useContext(PlayerContext).get("playAudio");
    const playerOnDesktop = useContext(PlayerContext).get("playerOnDesktop");
    const fromMaker = useContext(PlayerContext).get("fromMaker");
    const fromDemo = useContext(PlayerContext).get("fromDemo");
    const highlight = useContext(PlayerContext).get("highlight");
    const idPrefix = fromDemo ? (fromDemo +"-") : "";

    const [, animate] = useAnimate();

    useEffect(() => {
        if (highlight != null){

            let elementId = null;
            
            if (highlight.includes("chat")){
                elementId = "secondary-btn-chat";
            }            

            const element = document.getElementById(elementId);
            if (elementId != null && element != null){                   
                const animation = async () => {
                    await animate(element, { background: "#FFFFFFFF"}, {duration: 0.6});
                    if (element != null)
                        animate(element, { background: "#FFFFFF00"}, {duration: 0.6});
                }
                
                animation();
            }
        }
      }, [highlight, animate]);

    // Fonction pour extraire le premier URI Google Maps disponible depuis les messages
    const getGoogleMapsUri = () => {
        for (const msg of chatMessages) {
            if (msg.role === 'model' && msg.groundingMetadata?.groundingChunks) {
                const chunkWithMaps = msg.groundingMetadata.groundingChunks.find(chunk => chunk.maps?.uri);
                if (chunkWithMaps?.maps?.uri) {
                    return chunkWithMaps.maps.uri;
                }
            }
        }
        return null;
    };

    // Initialiser les messages au premier chargement
    useEffect(() => {
        if (isOpened && chatMessages.length === 0) {
            const initialMessages = props.story?.chatMessages || [
                {
                    role: "model",
                    textKey: "ChatWelcomeMessage"
                }
            ];
            setChatMessages(initialMessages);
        }
    }, [isOpened, props.story, chatMessages.length]);

    const scrollMessagesToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    // Scroll automatique vers le bas quand de nouveaux messages arrivent
    useEffect(() => {
        scrollMessagesToBottom();
    }, [chatMessages, isLoading]);

    // Scroll en bas à l'ouverture du chat (après montage + animation du bottom sheet)
    useEffect(() => {
        if (activeFeature === "chat" && props.story?.posId && !isOpened) {
            setIsOpened(true);
        }
    }, [activeFeature, props.story?.posId, isOpened]);

    useEffect(() => {
        if (!isOpened || activeFeature !== "chat") {
            return;
        }

        scrollMessagesToBottom();
        const t1 = setTimeout(scrollMessagesToBottom, 50);
        const t2 = setTimeout(scrollMessagesToBottom, 300);
        const t3 = setTimeout(scrollMessagesToBottom, 600);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, [isOpened, activeFeature, chatMessages.length]);

    useEffect(() => {
        if (props.story && props.show && activeFeature === "chat"){   
            const galleryOverlay = document.getElementById(idPrefix+"gallery-overlay");
            if (galleryOverlay != null){                                  
                const animation = async () => {                       
                    animate(galleryOverlay, { opacity: 1}, {duration: 0.3});                    
                }
                
                animation();
            }                        
        }
    }, [props.story, props.show, isOpened, activeFeature, animate, idPrefix]);

    // Gérer l'apparition du clavier mobile avec visualViewport (uniquement sur Android)
    useEffect(() => {
        if (!isOpened || !isAndroid()) {
            setKeyboardHeight(0);
            return;
        }

        const updateKeyboardHeight = () => {
            if (window.visualViewport) {
                // Calculer la hauteur du clavier : différence entre window.innerHeight et visualViewport.height
                const keyboardHeight = window.innerHeight - window.visualViewport.height;
                setKeyboardHeight(Math.max(0, keyboardHeight));
            } else {
                // Fallback pour les navigateurs qui ne supportent pas visualViewport
                setKeyboardHeight(0);
            }
        };

        // Mettre à jour au chargement
        updateKeyboardHeight();

        // Écouter les changements du visualViewport
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', updateKeyboardHeight);
            window.visualViewport.addEventListener('scroll', updateKeyboardHeight);
        } else {
            // Fallback : écouter les changements de window
            window.addEventListener('resize', updateKeyboardHeight);
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', updateKeyboardHeight);
                window.visualViewport.removeEventListener('scroll', updateKeyboardHeight);
            } else {
                window.removeEventListener('resize', updateKeyboardHeight);
            }
        };
    }, [isOpened]);

    if (props.story.posId == null && !hasChainRestaurants(props.story)) return (null);

        

    const open = () => {                
        if (!fromMaker /*&& !fromDemo*/ && !playerOnDesktop && !audioAlreadyDisabled && !playAudio){                        
            const ambiantPlayer = document.getElementById(idPrefix + "audio-player");
            if (ambiantPlayer && ambiantPlayer.paused){
                try {
                    ambiantPlayer.play();                
                } catch(error){}
            }
            setPlayAudio(true);            
        }
        pauseVideo();
        openFullscreen();
        setActiveFeature("chat");
        if (!(hasChainRestaurants(props.story) && !props.story.posId)) {
            setIsOpened(true);
        }
        /*if (playMode === "demo" || props.storyId != null && isCafeUprealityDemoStoryId(props.storyId)){
            setFrameOpened(true);
        }*/
      
        analyticsManager.logCheckChatTag(props.storyId, playMode, true);
                
        if (appAnalyticsManager)
            appAnalyticsManager.clickedChat();

        if (getStoredPixelConsent() === 'accepted') {
            if (window.ttq){
                window.ttq.track('ViewContent', {                
                    "contents": [
                        {              
                            "content_id": "0",           
                            "content_name": "chat",
                        }
                    ],
                });
            }
            if (window.fbq){                    
                window.fbq('track', 'ViewContent', {"content_name": "chat"});
            }
        }
      };

      const close = () => {        
        propagateFocusChange(true);
        setIsOpened(false);
        setActiveFeature(null);
        setFrameOpened(false);
        analyticsManager.logCheckChatTag(props.storyId, playMode, false);
      };

      const sendMessage = async () => {
        analyticsManager.logAskChatTag(props.storyId, playMode);
        if (!message.trim() || isLoading) return;

        const userMessage = message.trim();
        // Un event pixel par message envoyé (sans inclure le contenu texte, uniquement des signaux non sensibles).
        if (getStoredPixelConsent() === 'accepted') {
            try {
                if (window.ttq) {
                    window.ttq.track('ChatMessageSent', {
                        message_length: String(userMessage.length),
                    });
                }
            } catch (e) {
                // Ignorer si TikTok n'est pas disponible
            }
            try {
                if (window.fbq) {
                    window.fbq('trackCustom', 'ChatMessageSent', {
                        message_length: String(userMessage.length),
                    });
                }
            } catch (e) {
                // Ignorer si Meta n'est pas disponible
            }
        }
        setMessage("");

        // Ajouter le message utilisateur immédiatement
        const newUserMessage = {
            role: "user",
            text: userMessage
        };
        setChatMessages(prev => [...prev, newUserMessage]);

        // Mettre à jour l'historique pour l'envoyer à la fonction
        const updatedHistory = [...conversationHistory, newUserMessage];
        setConversationHistory(updatedHistory);

        // Afficher le loader
        setIsLoading(true);

        try {
            // Détecter la langue du navigateur
            const language = browserLocales(true)[0] === "fr" ? "fr" : "en";
            
            const storyContext = buildAnswerChatStoryContext(props.story);
            if (!storyContext) {
                setChatMessages(prev => [
                    ...prev.slice(0, -1),
                    { role: "model", text: t('ChainChooseRestaurant') },
                ]);
                setConversationHistory(prev => prev.slice(0, -1));
                setIsLoading(false);
                return;
            }

            // Appeler la callable function
            const answerChat = httpsCallable(functions, "answerChat");
            const result = await answerChat({
                message: userMessage,
                storyId: props.storyId,
                storyContext,
                history: updatedHistory,
                ipHash: ipHash,
                anchoredCount: anchoredCount,
                language: language
            });

            // Mettre à jour anchoredCount avec la valeur retournée par le backend
            if (result.data.anchoredCount !== undefined) {
                setAnchoredCount(result.data.anchoredCount);
            }

            // Enregistrer la conversation dans Firestore
            try {
                const db = getFirestore();
                const restaurantMessage = result.data.limitReached 
                    ? t("ChatLimitReached")
                    : result.data.text;
                
                await addDoc(collection(db, "support_chats"), {
                    storyId: props.storyId,
                    date: new Date(),
                    ipHash: ipHash,
                    clientMsg: userMessage,
                    restaurantMsg: restaurantMessage
                });
            } catch (error) {
                Sentry.captureMessage(printObj(error));                
                // Ne pas bloquer l'utilisateur en cas d'erreur d'enregistrement
            }

            // Vérifier si la limite est atteinte
            if (result.data.limitReached) {
                const limitMessage = {
                    role: "model",
                    textKey: "ChatLimitReachedWithLink",
                    website: props.story.website || null
                };
                setChatMessages(prev => [...prev, limitMessage]);
                
                // Mettre à jour l'historique si fourni par le backend (même si limitReached)
                if (result.data.history) {
                    setConversationHistory(result.data.history);
                }
            } else {
                // Ajouter la réponse du restaurant avec les métadonnées d'ancrage
                const modelMessage = {
                    role: "model",
                    text: result.data.text,
                    groundingMetadata: result.data.groundingMetadata || null
                };
                setChatMessages(prev => [...prev, modelMessage]);

                // Mettre à jour l'historique avec la réponse
                if (result.data.history) {
                    setConversationHistory(result.data.history);
                } else {
                    setConversationHistory(prev => [...prev, modelMessage]);
                }
            }
        } catch (error) {            
            Sentry.captureMessage(printObj(error));
            // Vérifier si c'est une erreur 429 (quota dépassé)
            const isQuotaError = error.code === 429 ||
                (error.error && error.error.code === 429) ||
                (error.status === "RESOURCE_EXHAUSTED") ||
                (error.message && error.message.includes("429")) ||
                (error.message && error.message.includes("quota"));
            
            // Enregistrer la conversation dans Firestore même en cas d'erreur
            try {
                const db = getFirestore();
                const restaurantMessage = isQuotaError ? t("ChatLimitReached") : t("ChatError");
                
                await addDoc(collection(db, "support_chats"), {
                    storyId: props.storyId,
                    date: new Date(),
                    ipHash: ipHash,
                    clientMsg: userMessage,
                    restaurantMsg: restaurantMessage
                });
            } catch (saveError) {
                Sentry.captureMessage(printObj(error));
                // Ne pas bloquer l'utilisateur en cas d'erreur d'enregistrement
            }
            
            // Afficher un message d'erreur
            const errorMessage = {
                role: "model",
                textKey: isQuotaError ? "ChatLimitReachedWithLink" : "ChatError",
                website: isQuotaError ? (props.story?.website || null) : null
            };
            setChatMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
      };    

      const language = browserLocales(true)[0] === "fr" ? "fr" : "en";
    
      if (props.show){                          
        if (isOpened && activeFeature === "chat"){
           /*if ((playMode === "demo" || props.storyId != null && isCafeUprealityDemoStoryId(props.storyId)) && frameOpened){
            return (<WebPageMock url={language === "fr" ? "/images/chat-demo.png" : "/images/chat-demo-en.png"} imageOverWebsite="true" close={close} open={frameOpened} width={storyDimensions.containerWidth} height={storyDimensions.containerHeight}/>);
           }
           else {*/
            return (
                <div style={{width: storyDimensions.containerWidth, height: storyDimensions.containerHeight, position: 'relative', overflow: 'hidden'}} onClick={(e)=>e.stopPropagation()}>
                    <div id={idPrefix + "gallery-overlay"} className="BlockingOverlay" style={{width: storyDimensions.containerWidth, height: storyDimensions.containerHeight, opacity: 0, background: "#FFFFFFD0", pointerEvents: 'auto'}} onClick={close}/>
                    
                    {/* Bottom Sheet personnalisé */}
                    <AnimatePresence>
                        {isOpened && (
                            <motion.div
                                ref={bottomSheetRef}
                                initial={{ y: storyDimensions.containerHeight * 0.7, x: '-50%' }}
                                animate={{ y: 0, x: '-50%' }}
                                exit={{ y: storyDimensions.containerHeight * 0.7, x: '-50%' }}
                                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                onAnimationComplete={() => {
                                    if (isOpened) {
                                        scrollMessagesToBottom();
                                    }
                                }}
                                style={{
                                    position: 'absolute',
                                    bottom: keyboardHeight,
                                    left: '50%',
                                    width: storyDimensions.containerWidth,
                                    maxWidth: '100vw',
                                    height: storyDimensions.containerHeight * 0.7,
                                    backgroundColor: '#FFFFFF',
                                    borderTopLeftRadius: 24 * sizeMultiplier,
                                    borderTopRightRadius: 24 * sizeMultiplier,
                                    zIndex: 1000,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
                                    transition: 'bottom 0.3s ease-out'
                                }}
                            >
                                <div style={{
                                    width: '100%',
                                    height: '100%',                                    
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden'
                                }}>
                                    {/* Header avec icône, nom et bouton fermer */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: 16 * sizeMultiplier,
                                        borderBottom: '1px solid #e0e0e0'
                                    }}>
                                        {/* Avatar du restaurant */}
                                        <div style={{
                                            marginRight: 12 * sizeMultiplier
                                        }}>
                                            {props.story.avatarUrl ? (
                                                <img 
                                                    alt={t('RestaurantAvatarAlt')}
                                                    src={props.story.avatarUrl} 
                                                    className="FBAvatarContainer" 
                                                    style={{
                                                        width: 40 * sizeMultiplier, 
                                                        height: 40 * sizeMultiplier
                                                    }} 
                                                />
                                            ) : (
                                                <img 
                                                    alt={t('RestaurantAvatarPlaceholderAlt')}
                                                    src="/images/avatar_placeholder.png" 
                                                    className="FBAvatarContainer" 
                                                    style={{
                                                        background: '#FFFFFF', 
                                                        width: 40 * sizeMultiplier, 
                                                        height: 40 * sizeMultiplier
                                                    }} 
                                                />
                                            )}
                                        </div>
                                        
                                        {/* Nom du restaurant */}
                                        <div style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2 * sizeMultiplier
                                        }}>
                                            <div style={{
                                                fontSize: 16 * sizeMultiplier,
                                                fontWeight: 'bold',
                                                fontFamily: 'Roboto, sans-serif',
                                                color: '#000000'
                                            }}>
                                                {props.story.posName}
                                            </div>
                                            {(() => {
                                                const googleMapsUri = getGoogleMapsUri();
                                                return googleMapsUri ? (
                                                    <a
                                                        onClick={() => analyticsManager.logCheckGMapsFromChatTag(props.storyId, playMode)}
                                                        href={googleMapsUri}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            fontSize: 12 * sizeMultiplier,
                                                            fontStyle: 'italic',
                                                            fontFamily: 'Roboto, sans-serif',
                                                            color: '#808080',
                                                            textDecoration: 'none',
                                                            cursor: 'pointer'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                    >
                                                        powered by Google Maps
                                                    </a>
                                                ) : (
                                                    <div style={{
                                                        fontSize: 11 * sizeMultiplier,
                                                        fontStyle: 'italic',
                                                        fontFamily: 'Roboto, sans-serif',
                                                        color: '#808080'
                                                    }}>
                                                        powered by Google Maps
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        
                                        {/* Bouton fermer */}
                                        <IconButton
                                            onClick={close}
                                            style={{
                                                padding: 8 * sizeMultiplier,                                                
                                                minWidth: 40 * sizeMultiplier,
                                                minHeight: 40 * sizeMultiplier
                                            }}
                                        >
                                            <CloseIcon style={{ fontSize: 24 * sizeMultiplier, color: '#a0a0a0' }} />
                                        </IconButton>
                                    </div>
                                    
                                    {/* Zone des messages */}
                                    <div 
                                        ref={messagesContainerRef}
                                        style={{
                                            flex: 1,
                                            overflowY: 'auto',
                                            padding: 16 * sizeMultiplier,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 12 * sizeMultiplier,
                                            position: 'relative'
                                        }}
                                    >
                                        {chatMessages.map((msg, index) => (
                                            <div key={index} style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'flex-start',
                                                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                                gap: 8 * sizeMultiplier
                                            }}>
                                                {/* Avatar pour les messages du restaurant */}
                                                {msg.role === 'model' && (
                                                    <div style={{ flexShrink: 0 }}>
                                                        {props.story.avatarUrl ? (
                                                            <img 
                                                                alt={t('RestaurantAvatarAlt')}
                                                                src={props.story.avatarUrl} 
                                                                className="FBAvatarContainer" 
                                                                style={{
                                                                    width: 32 * sizeMultiplier, 
                                                                    height: 32 * sizeMultiplier
                                                                }} 
                                                            />
                                                        ) : (
                                                            <img 
                                                                alt={t('RestaurantAvatarPlaceholderAlt')}
                                                                src="/images/avatar_placeholder.png" 
                                                                className="FBAvatarContainer" 
                                                                style={{
                                                                    background: '#FFFFFF', 
                                                                    width: 32 * sizeMultiplier, 
                                                                    height: 32 * sizeMultiplier
                                                                }} 
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {/* Bulle de message */}
                                                <div style={{
                                                    maxWidth: '70%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 8 * sizeMultiplier
                                                }}>
                                                    <div style={{
                                                        padding: `12px ${16 * sizeMultiplier}px`,
                                                        borderRadius: 18 * sizeMultiplier,
                                                        backgroundColor: msg.role === 'user' ? '#424242' : '#F5F5F5',
                                                        color: msg.role === 'user' ? '#FFFFFF' : '#000000',
                                                        fontSize: 16 * sizeMultiplier,
                                                        fontFamily: 'Roboto, sans-serif',
                                                        lineHeight: 1.4
                                                    }}>
                                                        {msg.textKey ? (
                                                            msg.website ? (() => {
                                                                const translation = t(msg.textKey);
                                                                const parts = translation.split(/<link>(.*?)<\/link>/);
                                                                return (
                                                                    <>
                                                                        {parts.map((part, index) => {
                                                                            if (index % 2 === 0) {
                                                                                return <span key={index}>{part}</span>;
                                                                            } else {
                                                                                return (
                                                                                    <a
                                                                                        key={index}
                                                                                        href={msg.website}
                                                                                        onClick={() => analyticsManager.logCheckWebsiteFromChatTag(props.storyId, playMode)}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        style={{
                                                                                            color: msg.role === 'user' ? '#FFFFFF' : '#000000',
                                                                                            textDecoration: 'underline',
                                                                                            cursor: 'pointer'
                                                                                        }}
                                                                                    >
                                                                                        {part}
                                                                                    </a>
                                                                                );
                                                                            }
                                                                        })}
                                                                    </>
                                                                );
                                                            })() : (
                                                                t(msg.textKey.replace("WithLink", ""))
                                                            )
                                                        ) : (
                                                            msg.text
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Espace pour aligner les messages utilisateur à droite */}
                                                {msg.role === 'user' && (
                                                    <div style={{ width: 32 * sizeMultiplier, flexShrink: 0 }} />
                                                )}
                                            </div>
                                        ))}
                                        
                                        {/* Message d'attente avec loader */}
                                        {isLoading && (
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'flex-start',
                                                justifyContent: 'flex-start',
                                                gap: 8 * sizeMultiplier
                                            }}>
                                                <div style={{ flexShrink: 0 }}>
                                                    {props.story.avatarUrl ? (
                                                        <img 
                                                            alt={t('RestaurantAvatarAlt')}
                                                            src={props.story.avatarUrl} 
                                                            className="FBAvatarContainer" 
                                                            style={{
                                                                width: 32 * sizeMultiplier, 
                                                                height: 32 * sizeMultiplier
                                                            }} 
                                                        />
                                                    ) : (
                                                        <img 
                                                            alt={t('RestaurantAvatarPlaceholderAlt')}
                                                            src="/images/avatar_placeholder.png" 
                                                            className="FBAvatarContainer" 
                                                            style={{
                                                                background: '#FFFFFF', 
                                                                width: 32 * sizeMultiplier, 
                                                                height: 32 * sizeMultiplier
                                                            }} 
                                                        />
                                                    )}
                                                </div>
                                                <div style={{
                                                    maxWidth: '70%',
                                                    padding: `12px ${16 * sizeMultiplier}px`,
                                                    borderRadius: 18 * sizeMultiplier,
                                                    backgroundColor: '#F5F5F5',
                                                    color: '#000000',
                                                    fontSize: 16 * sizeMultiplier,
                                                    fontFamily: 'Roboto, sans-serif',
                                                    lineHeight: 1.4,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8 * sizeMultiplier
                                                }}>
                                                    <CircularProgress size={16 * sizeMultiplier} style={{ color: '#666' }} />
                                                    <span>{t("ChatProcessing")}</span>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                        {/* Message de confidentialité en bas à droite */}
                                        <div style={{
                                            alignSelf: 'flex-end',
                                            marginTop: 'auto',
                                            marginBottom: 4 * sizeMultiplier,
                                            paddingTop: 8 * sizeMultiplier,
                                            fontSize: 12 * sizeMultiplier,
                                            fontStyle: 'italic',
                                            fontFamily: 'Roboto, sans-serif',
                                            color: '#808080',
                                            pointerEvents: 'none',
                                            paddingRight: 16 * sizeMultiplier,
                                            textAlign: 'right'
                                        }}>
                                            {t("ChatPrivacy")}
                                        </div>
                                        {(playMode === "play" || playMode === "review") && hasInstallablePixel(props.story) && getPersistedPixelConsent() === 'accepted' && (
                                            <div style={{
                                                alignSelf: 'flex-end',
                                                marginBottom: 4 * sizeMultiplier,
                                                fontSize: 12 * sizeMultiplier,
                                                fontStyle: 'italic',
                                                fontFamily: 'Roboto, sans-serif',
                                                paddingRight: 16 * sizeMultiplier,
                                                textAlign: 'right'
                                            }}>
                                                <span
                                                    onClick={requestReopenPixelConsent}
                                                    style={{
                                                        color: '#808080',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {t("ConsentManage")}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Champ de saisie en bas */}
                                    <div style={{
                                        padding: 16 * sizeMultiplier,
                                        borderTop: '1px solid #e0e0e0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8 * sizeMultiplier
                                    }}>
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            placeholder={t("ChatPlaceholder")}
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            onFocus={() => {
                                                // Faire défiler l'input en vue quand il reçoit le focus (uniquement sur Android)
                                                if (isAndroid()) {
                                                    setTimeout(() => {
                                                        if (inputRef.current) {
                                                            inputRef.current.scrollIntoView({ 
                                                                behavior: 'smooth', 
                                                                block: 'end',
                                                                inline: 'nearest'
                                                            });
                                                        }
                                                    }, 300);
                                                }
                                            }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && !isLoading) {
                                                    sendMessage();
                                                }
                                            }}
                                            disabled={isLoading}
                                            maxLength={200}
                                            style={{
                                                flex: 1,
                                                padding: `12px ${16 * sizeMultiplier}px`,
                                                borderRadius: 24 * sizeMultiplier,
                                                border: '1px solid #e0e0e0',
                                                fontSize: 16 * sizeMultiplier,
                                                fontFamily: 'Roboto, sans-serif',
                                                outline: 'none',
                                                opacity: isLoading ? 0.6 : 1
                                            }}
                                        />
                                        <button
                                            onClick={sendMessage}
                                            disabled={isLoading || !message.trim()}
                                            style={{
                                                width: 43 * sizeMultiplier,
                                                height: 43 * sizeMultiplier,
                                                borderRadius: '50%',
                                                backgroundColor: props.story.primaryColor,
                                                border: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: (isLoading || !message.trim()) ? 'not-allowed' : 'pointer',
                                                flexShrink: 0,
                                                opacity: (isLoading || !message.trim()) ? 0.6 : 1
                                            }}
                                        >
                                            <svg width={24 * sizeMultiplier} height={24 * sizeMultiplier} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill={props.story.secondaryColor}/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                                                                                    
                </div>
            );
            //}
        }                        
        else {                
            if (props.story != null){                  
                return (
                    <SecondaryButton buttonType={chatType} open={open} story={props.story}/>
                );
            }
            else {
                return null;
            }
        } 
    }       
        else {
            return null;
        }    
}

export default ChatButton;